import { atom } from "jotai";
import { floorToString } from "../utils/mimosysDataExtracter";
import { Trend } from "../types";
import dayjs, { Dayjs } from "dayjs";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import { fetchAllPaginated } from "../utils/fetchAllPaginated";

export type ResultChartData = {
  date: number;
  vitality100?: number;
  mental_activity100?: number;
  mental_activity_reliability?: boolean;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
  datingType?: string;
};

export type ChartInfoType = {
  data: ResultChartData[];
  latestVitalityTrend: Trend;
  vitalityAve: number;
  vitalitySdTop: number;
  vitalitySdBottom: number;

  latestMentalActivityTrend: Trend;
  mentalActivityAve: number;
  mentalActivitySdTop: number;
  mentalActivitySdBottom: number;

  mi1Ave: number;
  mi1NormalRangeTop: number;
  mi1NormalRangeBottom: number;

  recordingLastDates: number;
  outputStenScore: boolean;
};

export interface MimosysDateType {
  recorded_at: string;
  analyzed_at: string;
  mental_activity: number;
  mental_activity_reliability?: boolean;
  vitality: number;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
  file_id: string;
}

export interface MimosysResultDateType {
  recorded_at: string;
  mental_activity: number;
  mental_activity_reliability?: boolean;
  vitality: number;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
}

export const analysisStartedDateAtom = atom<Dayjs | null>((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  if (mimosysData.length > 0) {
    return dayjs(mimosysData[0].recorded_at);
  } else {
    return null;
  }
});

/** 最新のMi-1解析結果1件。未取得時は null */
export const latestMimosysAtom = atom<MimosysDateType | null>(null);

export const latestDateAtom = atom<Dayjs | null>((get) => {
  const latest = get(latestMimosysAtom);
  return latest ? dayjs(latest.recorded_at) : null;
});

export const latestStenScoreAtom = atom((get) => {
  const latest = get(latestMimosysAtom);
  return latest && latest.mi1_result !== undefined ? latest.mi1_result : null;
});

export const latestVitalityAtom = atom((get) => {
  return get(latestMimosysAtom)?.vitality ?? 0;
});
export const latestVitalityStringAtom = atom((get) => {
  return floorToString(get(latestVitalityAtom));
});

export const latestMentalActivityAtom = atom((get) => {
  return get(latestMimosysAtom)?.mental_activity ?? 0;
});

export const latestMentalActivityStringAtom = atom((get) => {
  return floorToString(get(latestMentalActivityAtom));
});

export const latestMi1ResultReliabilityAtom = atom((get) => {
  return get(latestMimosysAtom)?.mi1_result_reliability;
});

export const latestMentalActivityReliabilityAtom = atom((get) => {
  return get(latestMimosysAtom)?.mental_activity_reliability;
});

export const MimosysDataInitial: MimosysDateType[] = [];
export const MimosysDataAtom = atom(MimosysDataInitial);

/** recorded_at 昇順 */
export const MimosysDataAscAtom = atom((get) => {
  const data = get(MimosysDataAtom);
  return [...data].sort(
    (a, b) =>
      new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime(),
  );
});

export const isGetMimosysDataAtom = atom(false);

/** vitality / mental_activity の母標準偏差(0〜100スケール) */
export type ScoreSd = {
  vitalitySd: number;
  mentalActivitySd: number;
};

export const ScoreSdInitial: ScoreSd = { vitalitySd: 0, mentalActivitySd: 0 };

export const scoreSdAtom = atom<ScoreSd>(ScoreSdInitial);

/** vitality / mental_activity の平均(0〜1スケール) */
export type ScoreAve = {
  vitalityAve: number;
  mentalActivityAve: number;
};

export const ScoreAveInitial: ScoreAve = {
  vitalityAve: 0,
  mentalActivityAve: 0,
};

export const scoreAveAtom = atom<ScoreAve>(ScoreAveInitial);

export type EmotionType = {
  ang: number;
  joy: number;
  clm: number;
  srw: number;
  exc: number;
};

export const latestEmotionAtom = atom<EmotionType>({
  ang: 0,
  joy: 0,
  clm: 0,
  srw: 0,
  exc: 0,
});

/** Mi-1解析結果一覧を全件取得し、recorded_at 昇順の MimosysDateType[] に変換して返す */
export async function fetchMimosysDataList(): Promise<MimosysDateType[]> {
  const mi1List = await fetchAllPaginated("/v2/core/analysis/mi1/list", {
    params: { query: { detail: false } },
  });
  return mi1List
    .map((item) => ({
      recorded_at: item.recorded_at,
      analyzed_at: item.analyzed_at,
      mental_activity: item.analysis_result.mental_activity,
      mental_activity_reliability:
        item.analysis_result.mental_activity_reliability ?? undefined,
      vitality: item.analysis_result.vitality,
      mi1_result: item.analysis_result.mi1_result ?? undefined,
      mi1_result_reliability:
        item.analysis_result.mi1_result_reliability ?? undefined,
      file_id: item.recording_id,
    }))
    .sort((a, b) => {
      if (dayjs(a.recorded_at).isBefore(dayjs(b.recorded_at))) return -1;
      if (dayjs(a.recorded_at).isAfter(dayjs(b.recorded_at))) return 1;
      return 0;
    });
}

/** Mi-1解析結果の最新1件を取得して MimosysDateType に変換して返す。結果がなければ null */
export async function fetchLatestMimosysData(): Promise<MimosysDateType | null> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/list",
    {
      params: { query: { detail: false, limit: 1 } },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  if (data.length === 0) return null;
  const item = data[0];
  return {
    recorded_at: item.recorded_at,
    analyzed_at: item.analyzed_at,
    mental_activity: item.analysis_result.mental_activity,
    mental_activity_reliability:
      item.analysis_result.mental_activity_reliability ?? undefined,
    vitality: item.analysis_result.vitality,
    mi1_result: item.analysis_result.mi1_result ?? undefined,
    mi1_result_reliability:
      item.analysis_result.mi1_result_reliability ?? undefined,
    file_id: item.recording_id,
  };
}

/** 指定フィールドの母標準偏差を aggregate API から取得する(0〜1スケール) */
async function fetchStddevPop(
  field: "vitality" | "mental_activity",
): Promise<number> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/aggregate",
    {
      params: {
        query: {
          $apply: `aggregate(analysis_result/${field} with Voislog.stddev_pop as sd)`,
        },
      },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  return data.sd ?? 0;
}

/**
 * Mi-1解析結果の vitality / mental_activity の母標準偏差を取得する。
 * 値は 0〜1 スケールで返るため、表示スケール(0〜100)に合わせて 100 倍して返す。
 */
export async function fetchScoreSd(): Promise<ScoreSd> {
  const [vitalitySd, mentalActivitySd] = await Promise.all([
    fetchStddevPop("vitality"),
    fetchStddevPop("mental_activity"),
  ]);
  return {
    vitalitySd: vitalitySd * 100,
    mentalActivitySd: mentalActivitySd * 100,
  };
}

/** 指定フィールドの平均を aggregate API から取得する(0〜1スケール) */
async function fetchAverage(
  field: "vitality" | "mental_activity",
): Promise<number> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/aggregate",
    {
      params: {
        query: {
          $apply: `aggregate(analysis_result/${field} with average as ave)`,
        },
      },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  return data.ave ?? 0;
}

/**
 * Mi-1解析結果の vitality / mental_activity の平均を取得する。
 * 値は 0〜1 スケールで返る。
 */
export async function fetchScoreAve(): Promise<ScoreAve> {
  const [vitalityAve, mentalActivityAve] = await Promise.all([
    fetchAverage("vitality"),
    fetchAverage("mental_activity"),
  ]);
  return {
    vitalityAve,
    mentalActivityAve,
  };
}

type fetchEmotionsProps = {
  recording_id: string;
};

type fetchEmotionsValue = {
  emotions: EmotionType[];
};

export async function fetchEmotions({
  recording_id,
}: fetchEmotionsProps): Promise<fetchEmotionsValue | null> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/recordings/{recording_id}",
    {
      params: { path: { recording_id } },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  return {
    emotions: data.detailed_analysis_result!.emotions,
  };
}

export async function fetchLatestEmotions(): Promise<fetchEmotionsValue | null> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/list",
    {
      params: { query: { detail: true, limit: 1 } },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  if (data.length === 0) return null;
  return {
    emotions: data[0].detailed_analysis_result!.emotions,
  };
}
